import React, { useState, useEffect } from 'react';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { TECH_SECTIONS } from '../data/techSections';

const navCls = 'text-sm font-medium text-white/80 hover:text-white transition-colors drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]';
const TECH_LINKS = [...TECH_SECTIONS.map((s) => [s.title, `/technology/${s.slug}`])];

const Logo = () => (
  <Link to="/" className="flex items-center gap-3 group" data-testid="logo-link">
    <span className="text-xl lg:text-2xl font-extrabold tracking-tight text-white drop-shadow-[0_1px_3px_rgba(0,0,0,0.8)]" style={{ fontFamily: "'Open Sans', sans-serif" }}>
      <span className="text-[#FF9933]">j</span><span className="text-white">and</span><span className="text-[#138808]">a</span>
      <span className="text-white">ta</span><span className="text-[#4169E1]">.</span><span className="text-white">ai</span>
    </span>
    <span className="hidden sm:block text-[11px] font-mono tracking-[0.2em] uppercase text-white/60 font-semibold">Embodied Data Engine</span>
  </Link>
);

const TechMenu = ({ active }) => (
  <div className="relative group" data-testid="nav-technology-menu">
    <Link to="/technology/capture" className={`${navCls} inline-flex items-center gap-1 ${active ? 'text-white' : ''}`} data-testid="nav-technology">
      Technology <ChevronDown size={14} className="transition-transform duration-200 group-hover:rotate-180" />
    </Link>
    <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible group-focus-within:opacity-100 group-focus-within:visible transition-opacity duration-200">
      <div className="w-60 rounded-2xl border border-white/10 bg-[#111111]/95 backdrop-blur-xl p-2 shadow-2xl" data-testid="nav-technology-dropdown">
        {TECH_LINKS.map(([label, to]) => (
          <NavLink key={to} to={to} end data-testid={`nav-tech-${to.split('/').pop()}`}
            className={({ isActive }) => `block rounded-lg px-3 py-2 text-sm transition-colors ${isActive ? 'bg-[#FF7A00]/10 text-[#FF7A00]' : 'text-white/70 hover:bg-white/[0.05] hover:text-white'}`}>
            {label}
          </NavLink>
        ))}
      </div>
    </div>
  </div>
);

const MobileNav = ({ close }) => (
  <div className="md:hidden pb-6 pt-4 border-t border-white/10 flex flex-col gap-4 bg-[#0A0A0A]/85 backdrop-blur-xl" data-testid="mobile-nav">
    <Link to="/" className="text-sm text-white/70" onClick={close}>Home</Link>
    <Link to="/data/showcase" className="text-sm text-white/70" onClick={close} data-testid="mobile-nav-showcase">Data Showcase</Link>
    <div>
      <Link to="/technology/capture" className="text-sm text-white/70" onClick={close} data-testid="mobile-nav-technology">Technology</Link>
      <div className="mt-2 ml-3 pl-3 border-l border-white/10 flex flex-col gap-2">
        {TECH_SECTIONS.map((s) => <Link key={s.slug} to={`/technology/${s.slug}`} onClick={close} className="text-sm text-white/45">{s.title}</Link>)}
      </div>
    </div>
    <Link to="/#use-cases" className="text-sm text-white/70" onClick={close} data-testid="mobile-nav-use-cases">Use Cases</Link>
    <Link to="/contact" onClick={close} className="mt-2 btn-gradient px-5 py-2.5 text-sm font-semibold rounded-full w-fit" data-testid="mobile-cta">Request Data</Link>
  </div>
);

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const goUseCases = (e) => {
    if (pathname !== '/') return;
    e.preventDefault();
    document.getElementById('use-cases')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <header data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${isScrolled || isMobileOpen ? 'bg-[#0A0A0A]/95 backdrop-blur-xl border-b border-white/10' : 'bg-transparent'}`}>
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          <Logo />
          <nav className="hidden md:flex items-center gap-8">
            <Link to="/data/showcase" className={`${navCls} ${pathname.startsWith('/data') ? 'text-white' : ''}`} data-testid="nav-datasets">Data Showcase</Link>
            <TechMenu active={pathname.startsWith('/technology')} />
            <Link to="/#use-cases" onClick={goUseCases} className={navCls} data-testid="nav-use-cases">Use Cases</Link>
            <Link to="/contact" className="btn-gradient px-5 py-2 text-sm font-semibold rounded-full" data-testid="nav-cta">Request Data</Link>
          </nav>
          <button className="md:hidden text-white" onClick={() => setIsMobileOpen(!isMobileOpen)} data-testid="mobile-menu-toggle" aria-label="Toggle menu">
            {isMobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
        {isMobileOpen && <MobileNav close={() => setIsMobileOpen(false)} />}
      </div>
    </header>
  );
};

export default Header;
